import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { supabase } from '../../lib/supabase'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { DollarSign, Filter, Download, Building2 } from 'lucide-react'

interface PaymentRecord {
  order_id: string
  tenant_name: string
  user_email: string
  course_title: string
  amount: number
  price_type: 'standard' | 'retry'
  purchased_at: string
  stripe_payment_intent_id: string | null
}

export function PaymentsManagePage() {
  useDocumentTitle('決済管理')
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isPlatformAdmin } = useTenant()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [payments, setPayments] = useState<PaymentRecord[]>([])
  const [filterTenant, setFilterTenant] = useState<string>('all')
  const [tenants, setTenants] = useState<{ tenant_id: string; name: string }[]>([])

  useEffect(() => {
    if (!isPlatformAdmin) {
      navigate('/mypage')
      return
    }

    if (user) {
      fetchPaymentsData()
    }
  }, [user, isPlatformAdmin])

  const fetchPaymentsData = async () => {
    try {
      setLoading(true)
      setError(null)

      // テナント一覧を取得
      const { data: tenantsData } = await supabase
        .from('tenants')
        .select('tenant_id, name')
        .eq('status', 'active')
        .order('name')

      setTenants(tenantsData || [])

      // 完了済み注文を取得
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          order_id,
          tenant_id,
          user_id,
          course_id,
          amount,
          price_type,
          purchased_at,
          stripe_payment_intent_id
        `)
        .eq('status', 'completed')
        .order('purchased_at', { ascending: false })

      if (ordersError) throw ordersError

      // 関連データを取得
      const tenantIds = [...new Set(ordersData?.map((o) => o.tenant_id) || [])]
      const userIds = [...new Set(ordersData?.map((o) => o.user_id) || [])]
      const courseIds = [...new Set(ordersData?.map((o) => o.course_id) || [])]

      const { data: tenantsMap } = await supabase
        .from('tenants')
        .select('tenant_id, name')
        .in('tenant_id', tenantIds)

      const { data: usersMap } = await supabase
        .from('users')
        .select('user_id, email')
        .in('user_id', userIds)

      const { data: coursesMap } = await supabase
        .from('courses')
        .select('course_id, title')
        .in('course_id', courseIds)

      const tenantsLookup = new Map(tenantsMap?.map((t) => [t.tenant_id, t.name]))
      const usersLookup = new Map(usersMap?.map((u) => [u.user_id, u.email]))
      const coursesLookup = new Map(coursesMap?.map((c) => [c.course_id, c.title]))

      // データを整形
      const paymentRecords: PaymentRecord[] = (ordersData || []).map((order) => ({
        order_id: order.order_id,
        tenant_name: tenantsLookup.get(order.tenant_id) || '不明',
        user_email: usersLookup.get(order.user_id) || '不明',
        course_title: coursesLookup.get(order.course_id) || '不明',
        amount: order.amount,
        price_type: order.price_type,
        purchased_at: order.purchased_at,
        stripe_payment_intent_id: order.stripe_payment_intent_id,
      }))

      setPayments(paymentRecords)
    } catch (err: any) {
      console.error('Error fetching payments:', err)
      setError(err.message || '決済データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const filteredPayments = payments.filter((p) => {
    if (filterTenant === 'all') return true
    const tenant = tenants.find((t) => t.name === p.tenant_name)
    return tenant?.tenant_id === filterTenant
  })

  const handleExportCSV = () => {
    // CSV出力のヘッダー
    const headers = [
      '購入日時',
      'テナント',
      '購入者メール',
      '講座名',
      '種別',
      '金額',
      'Stripe Payment Intent ID',
    ]

    // CSV出力のデータ行
    const rows = filteredPayments.map((p) => [
      new Date(p.purchased_at).toLocaleString('ja-JP'),
      p.tenant_name,
      p.user_email,
      p.course_title,
      p.price_type === 'standard' ? '通常' : '再受験',
      p.amount.toString(),
      p.stripe_payment_intent_id || '',
    ])

    // CSVテキスト生成
    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n')

    // BOMを追加（Excel対応）
    const bom = '\uFEFF'
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' })

    // ダウンロード
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `payments_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="決済データを読み込み中..." />
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">決済管理</h1>
          <p className="mt-2 text-gray-600">全テナントの決済履歴を確認します</p>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} onRetry={fetchPaymentsData} />
          </div>
        )}

        {/* フィルターとアクション */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">テナント:</span>
            </div>
            <select
              value={filterTenant}
              onChange={(e) => setFilterTenant(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">全て</option>
              {tenants.map((tenant) => (
                <option key={tenant.tenant_id} value={tenant.tenant_id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>

          {filteredPayments.length > 0 && (
            <button
              onClick={handleExportCSV}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition text-sm font-medium"
            >
              <Download className="h-4 w-4" />
              CSV出力
            </button>
          )}
        </div>

        {/* 統計サマリー */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">総決済数</p>
                <p className="text-2xl font-bold text-gray-900">{filteredPayments.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">総売上</p>
                <p className="text-2xl font-bold text-gray-900">
                  ¥{filteredPayments.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">平均購入額</p>
                <p className="text-2xl font-bold text-gray-900">
                  ¥
                  {filteredPayments.length > 0
                    ? Math.round(
                        filteredPayments.reduce((sum, p) => sum + p.amount, 0) /
                          filteredPayments.length
                      ).toLocaleString()
                    : 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 決済一覧 */}
        {filteredPayments.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">決済履歴がありません</h3>
            <p className="text-gray-600">購入完了後に決済履歴が表示されます</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    購入日時
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    テナント
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    購入者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    講座
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    種別
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    金額
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPayments.map((payment) => (
                  <tr key={payment.order_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(payment.purchased_at).toLocaleDateString('ja-JP')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(payment.purchased_at).toLocaleTimeString('ja-JP')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{payment.tenant_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{payment.user_email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{payment.course_title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded ${
                          payment.price_type === 'standard'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-orange-100 text-orange-800'
                        }`}
                      >
                        {payment.price_type === 'standard' ? '通常' : '再受験'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        ¥{payment.amount.toLocaleString()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MainLayout>
  )
}
